/**
 * A conferência do laudo e das orientações (E159.F3, GUS-153, ADR-0109 §2.6–§2.7). Espelha
 * `apps/api/types/conferencia_das_saidas.py` e `apps/api/types/conferencia_da_saida.py`.
 *
 * - `GET /casos/:id/saidas/conferencia`: cada saída conferida do caso (o laudo e, em PARTE, as
 *   orientações) com o último artefato, a conferência gravada, a situação e o que ela precisa.
 * - `POST /casos/:id/saidas/conferencia`: "Conferir agora" — emite, confere e devolve a mesma
 *   leitura.
 *
 * A tela não decide nada: a situação, o estado, os apontamentos e a pendência vêm do servidor.
 */
import axios, { type AxiosInstance } from 'axios';

export type SaidaConferida = 'laudo' | 'riscos';
export type SituacaoDaSaida = 'conferida' | 'desatualizada' | 'nao_conferida';
export type PendenciaDaSaida =
  | 'conferir'
  | 'corrigir'
  | 'revisar'
  | 'assinar'
  | 'liberar'
  | 'nenhuma';
export type MotivoDaConferencia = 'grafotecnica' | 'sem_resultado';
export type EstadoDaConferencia = 'aprovado' | 'corrigir' | 'bloqueado';
export type EstadoDoCriterio = 'passou' | 'falhou' | 'inconclusivo' | 'nao_se_aplica_justificado';
export type GrupoDoCriterio = 'campos' | 'afirmacoes' | 'protecao';
export type GravidadeDoApontamento = 'corrigir' | 'bloqueado';
export type OndeCorrigir = 'rever_na_fonte' | 'revisar_na_mesa' | 'cadastro_do_caso' | 'composicao';
export type EtapaDoArtefato = 'rascunho' | 'final';
export type FormatoDoArtefato = 'pdf' | 'docx';

export type ChaveDaConferencia =
  | 'identidade_do_caso'
  | 'modo_do_exame'
  | 'versao_do_resultado'
  | 'conclusao'
  | 'matriz_publica'
  | 'essencial_no_laudo'
  | 'riscos_do_reservado'
  | 'respostas_dos_riscos'
  | 'parte_e_cpf'
  | 'ficha_confere'
  | 'ausencia_contradita'
  | 'conclusao_registrada'
  | 'coerencia_da_conclusao'
  | 'decisao_refeita'
  | 'conclusao_na_parte'
  | 'resultado_unico'
  | 'contagem_dos_requisitos'
  | 'contagem_da_integridade'
  | 'papeis_coerentes'
  | 'vigencia_dos_dispositivos'
  | 'remissao_resolvida'
  | 'teste_executado'
  | 'consulta_executada'
  | 'carimbo_e_cadeia'
  | 'presenca_fisica'
  | 'posse_do_original'
  | 'autoria'
  | 'instante_presumido'
  | 'validade_ou_fraude'
  | 'revisao_registrada'
  | 'vocabulario_publico'
  | 'cpf_completo'
  | 'marcador_de_preenchimento'
  | 'reservado_fora_do_laudo'
  | 'aviso_do_reservado'
  | 'endereco_do_caso'
  | 'legivel_no_pdf'
  | 'grau_sem_marca'
  // E172.F17 (GUS-230 itens 6 e 8): o reservado lido contra o exame da mesma versão
  | 'diligencia_registrada'
  | 'marca_do_laudo'
  | 'resposta_pela_prova'
  | 'providencia_especifica'
  | 'sem_identificador_interno';

export interface ApontamentoData {
  id: string;
  chave: ChaveDaConferencia;
  gravidade: GravidadeDoApontamento;
  trecho: string;
  no_documento: string;
  na_fonte: string;
  fonte: string;
  onde_corrigir: OndeCorrigir;
}

export interface CriterioDaSaidaData {
  chave: ChaveDaConferencia;
  grupo: GrupoDoCriterio;
  estado: EstadoDoCriterio;
  bloqueante: boolean;
  detalhe: string;
}

export interface EntradaDaConferenciaData {
  documento_sha256: string;
  avaliacao_id: string;
  rodada: number;
  resultado_sha256: string;
  ficha_sha256: string | null;
  cadastro_sha256: string;
}

export interface ConferenciaDaSaidaData {
  versao: string;
  kind: SaidaConferida;
  estado: EstadoDaConferencia;
  criterios: CriterioDaSaidaData[];
  apontamentos: ApontamentoData[];
  limite: string;
  entrada: EntradaDaConferenciaData;
}

export interface ArtefatoConferidoData {
  id: string;
  versao: number;
  etapa: EtapaDoArtefato;
  formato: FormatoDoArtefato;
  rodada: number;
  resultado_sha256: string;
  criada_em: string;
}

export interface SaidaNaConferenciaData {
  kind: SaidaConferida;
  situacao: SituacaoDaSaida;
  pendencia: PendenciaDaSaida;
  artefato: ArtefatoConferidoData | null;
  conferencia: ConferenciaDaSaidaData | null;
  motivo: string | null;
}

export interface ConferenciaDasSaidasData {
  case_id: string;
  aplicavel: boolean;
  motivo: MotivoDaConferencia | null;
  rodada: number | null;
  versao_do_catalogo: string;
  limite: string;
  saidas: SaidaNaConferenciaData[];
}

export type MotivoDaFalha =
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'nao_aplicavel'
  | 'laudo_nao_pronto'
  | 'desatualizado'
  | 'indisponiveis'
  | 'falha_do_documento'
  | 'indisponivel';

export class ConferenciaDasSaidasError extends Error {
  constructor(readonly motivo: MotivoDaFalha) {
    super(motivo);
    this.name = 'ConferenciaDasSaidasError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const DETALHE: Record<string, MotivoDaFalha> = {
  CONFERENCIA_NAO_APLICAVEL: 'nao_aplicavel',
  LAUDO_ELETRONICO_NOT_READY: 'laudo_nao_pronto',
  RESULTADO_DESATUALIZADO: 'desatualizado',
  ORIENTACOES_INDISPONIVEIS: 'indisponiveis',
  LAUDO_RENDER_FAILED: 'falha_do_documento',
};

export function motivoDoErro(err: unknown): MotivoDaFalha {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  const status = err.response?.status;
  if (status === HTTP_FORBIDDEN) return 'sem_permissao';
  if (status === HTTP_NOT_FOUND) return 'nao_encontrado';
  const detalhe: unknown = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  const codigo =
    typeof detalhe === 'string' ? detalhe : (detalhe as { code?: string } | undefined)?.code;
  return (codigo !== undefined ? DETALHE[codigo] : undefined) ?? 'indisponivel';
}

/** "Conferir agora" compõe e converte o laudo e as orientações: a espera é a dos documentos. */
export const CONFERIR_AGORA_TIMEOUT_MS = 240_000;

export async function lerAConferenciaDasSaidas(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaDasSaidasData> {
  try {
    const { data } = await client.get<ConferenciaDasSaidasData>(
      `/casos/${caseId}/saidas/conferencia`,
    );
    return data;
  } catch (err) {
    throw new ConferenciaDasSaidasError(motivoDoErro(err));
  }
}

export async function conferirAgora(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaDasSaidasData> {
  try {
    const { data } = await client.post<ConferenciaDasSaidasData>(
      `/casos/${caseId}/saidas/conferencia`,
      undefined,
      { timeout: CONFERIR_AGORA_TIMEOUT_MS },
    );
    return data;
  } catch (err) {
    throw new ConferenciaDasSaidasError(motivoDoErro(err));
  }
}

/** Para onde a correção de um apontamento leva, na área da equipe (`null`: correção técnica). */
export function destinoDaCorrecao(onde: OndeCorrigir, caseId: string): string | null {
  switch (onde) {
    case 'rever_na_fonte':
      return `/casos/${caseId}/pre-analise`;
    case 'revisar_na_mesa':
      return `/casos/${caseId}/mesa-eletronica`;
    case 'cadastro_do_caso':
      return `/casos/${caseId}`;
    case 'composicao':
      return null;
  }
}
